import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, API, DOMIUM_SIMULATION_CREDIT_COST, getErrorMessage } from '../constants';
import { getToken } from '../../../services/tokenService';

export default function useSimulatorEngine({
  user, userRole, identite, operation, technique, etudes, intervenants,
  isOnline, saveToLocal, setSavedSimulation, setActiveTab, result: result, setResult,
  restoreDraftData,
}) {
  const isExemptFromCredits = useMemo(() => {
    const role = (user?.role || userRole || '').toLowerCase();
    return role === 'super_admin' || role === 'admin';
  }, [userRole, user?.role]);

  const isSuperAdmin = useMemo(() =>
    (user?.role || userRole || '').toLowerCase() === 'super_admin',
    [userRole, user?.role]
  );

  const token = getToken();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [isFtpeMember, setIsFtpeMember] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);

  // --- Config ---
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/config`);
        if (res.data.success) setConfig(res.data.config);
      } catch {}
    };
    fetchConfig();
  }, []);

  // --- FTPE Status ---
  useEffect(() => {
    const checkFtpeStatus = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API}/ftpe-adhesion/member/${user.id}/ftpe-status`, { headers });
        setIsFtpeMember(res.data?.has_ftpe === true && res.data?.status === 'adherent');
      } catch { setIsFtpeMember(false); }
    };
    checkFtpeStatus();
  }, [user, headers]);

  // --- Credits ---
  const fetchCreditBalance = useCallback(async () => {
    if (isExemptFromCredits) { setCreditBalance(null); return; }
    setCreditLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/wallets/balance`, { headers });
      if (res.data) setCreditBalance(res.data.credit_balance || 0);
    } catch { setCreditBalance(0); }
    finally { setCreditLoading(false); }
  }, [headers, isExemptFromCredits]);

  useEffect(() => { fetchCreditBalance(); }, [fetchCreditBalance]);

  const consumeCredits = async (amount, description) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/wallets/consume-credits`,
        { credits: amount, action_type: 'domium_simulation', description }, { headers });
      if (res.data.success) { setCreditBalance(res.data.new_balance); return true; }
      return false;
    } catch { return false; }
  };

  // --- Calculation ---
  const lastCalculationRef = useRef(null);
  const calculationTimeoutRef = useRef(null);

  const calculationKey = useMemo(() => JSON.stringify({
    cout: technique.cout_construction_ht, territoire: operation.territoire,
    type_op: operation.type_operation, type_bat: operation.type_batiment,
    prevention: technique.mesures_prevention, ess: technique.part_entreprises_ess,
  }), [technique.cout_construction_ht, technique.mesures_prevention,
    technique.part_entreprises_ess, operation.territoire,
    operation.type_operation, operation.type_batiment]);

  const handleCalculate = useCallback(async () => {
    // V36.58 — Le Super Admin peut lancer un calcul même avec un coût de
    // construction à 0 (les autres utilisateurs doivent saisir un coût > 0).
    if (!isSuperAdmin && (!technique.cout_construction_ht || technique.cout_construction_ht <= 0)) return;
    if (lastCalculationRef.current === calculationKey) return;
    lastCalculationRef.current = calculationKey;
    setCalculating(true);

    const getTaux = (montant) => {
      if (config?.tranches_taux_actives && config?.tranches_taux?.length > 0) {
        for (const t of config.tranches_taux) {
          const sMin = t.seuil_min || 0;
          const sMax = t.seuil_max;
          if (sMax === null || sMax === undefined) { if (montant >= sMin) return t.taux / 100; }
          else { if (montant >= sMin && montant <= sMax) return t.taux / 100; }
        }
      }
      return config?.taux_base ? config.taux_base / 100 : 0.012;
    };

    const coutHT = parseFloat(technique.cout_construction_ht) || 0;
    const tauxApplicable = getTaux(coutHT);
    const coeffTerritoire = config?.coeff_territoire?.[operation.territoire] || 1.15;
    const aff = config?.affectation || { fonds_garantie: 60, prevention_expertise: 25, gestion_solidaire: 15 };

    if (!isOnline) {
      try {
        const tauxEffectif = tauxApplicable * coeffTerritoire;
        const tauxFinal = Math.min(Math.max(tauxEffectif, (config?.taux_min || 0.8) / 100), (config?.taux_max || 1.8) / 100);
        const montant = coutHT * tauxFinal;
        let trancheUtilisee = null;
        if (config?.tranches_taux_actives && config?.tranches_taux?.length > 0) {
          for (const t of config.tranches_taux) {
            const sMin = t.seuil_min || 0; const sMax = t.seuil_max;
            if ((sMax === null || sMax === undefined) ? coutHT >= sMin : (coutHT >= sMin && coutHT <= sMax)) { trancheUtilisee = t; break; }
          }
        }
        const offlineResult = {
          success: true, offline_estimation: true, contribution_ht: montant,
          contribution: { montant_ht: montant, montant_ttc: montant * 1.085, base_calcul: coutHT, taux_applique: tauxFinal * 100 },
          calcul: {
            cout_construction_ht: coutHT, taux_base: tauxApplicable * 100, coeff_territoire: coeffTerritoire,
            taux_effectif: tauxEffectif * 100, taux_final: tauxFinal * 100, contribution_ht: montant,
            tranches_actives: config?.tranches_taux_actives || false,
            tranche_utilisee: trancheUtilisee ? { label: trancheUtilisee.label, seuil_min: trancheUtilisee.seuil_min, seuil_max: trancheUtilisee.seuil_max, taux_tranche: trancheUtilisee.taux } : null,
            garantie: { duree_ans: 10 },
          },
          affectation: { fonds_garantie: montant * (aff.fonds_garantie / 100), prevention_expertise: montant * (aff.prevention_expertise / 100), gestion_solidaire: montant * (aff.gestion_solidaire / 100) },
          message: 'Estimation hors ligne',
        };
        setResult(offlineResult);
        await saveToLocal({ type: 'domium', identite, operation, technique, result: offlineResult });
        toast.warning('Mode hors ligne: Estimation approximative', { description: 'Le calcul exact sera effectue a la reconnexion', duration: 4000 });
      } catch {} finally { setCalculating(false); }
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/calculate`, {
        cout_construction_ht: technique.cout_construction_ht, territoire: operation.territoire,
        type_operation: operation.type_operation, type_batiment: operation.type_batiment,
        mesures_prevention: technique.mesures_prevention, part_entreprises_ess: technique.part_entreprises_ess,
      }, { headers });
      if (res.data.success) {
        setResult(res.data);
        await saveToLocal({ type: 'domium', identite, operation, technique, result: res.data });
      }
    } catch {} finally { setCalculating(false); }
  }, [calculationKey, technique, operation, headers, isOnline, identite, saveToLocal, config, setResult]);

  useEffect(() => {
    if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current);
    // V36.58 — Le Super Admin déclenche le calcul même avec coût à 0.
    if (isSuperAdmin || technique.cout_construction_ht > 0) {
      calculationTimeoutRef.current = setTimeout(() => handleCalculate(), 800);
    }
    return () => { if (calculationTimeoutRef.current) clearTimeout(calculationTimeoutRef.current); };
  }, [calculationKey, isSuperAdmin]);

  // --- Simulations ---
  const [simulations, setSimulations] = useState([]);

  const fetchSimulations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/simulations`, { headers });
      if (res.data.success) setSimulations(res.data.simulations);
    } catch {}
  }, [headers]);

  useEffect(() => { fetchSimulations(); }, []);

  const handleLoadServerSimulation = async (reference) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
      if (res.data.success && res.data.simulation) {
        const sim = res.data.simulation;
        restoreDraftData(sim);
        // Restaurer le résultat du calcul — wrapper pour compatibilité avec ResultatTab
        if (sim.calcul && typeof sim.calcul === 'object') {
          setResult({
            success: true,
            calcul: sim.calcul,
            contribution_ht: sim.calcul.contribution_ht,
            affectation: sim.affectation || null,
          });
        }
        setSavedSimulation(sim);
        setActiveTab('resultat');
        toast.success(`Simulation ${reference} chargee`);
      } else { toast.error('Simulation non trouvee'); }
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const handleSaveSimulation = async (options = {}) => {
    const silent = options?.silent === true;
    if (!isExemptFromCredits) {
      if (creditBalance === null) await fetchCreditBalance();
      if (creditBalance !== null && creditBalance < DOMIUM_SIMULATION_CREDIT_COST) {
        toast.error(`Credits insuffisants. Necessite ${DOMIUM_SIMULATION_CREDIT_COST} credits. Solde: ${creditBalance}`, { duration: 5000 });
        return null;
      }
    }
    setSaving(true);
    try {
      const cleanedTechnique = {
        ...technique, cout_construction_ht: technique.cout_construction_ht || 0,
        surface_plancher: technique.surface_plancher || 0, nombre_batiments: technique.nombre_batiments || 1,
        nombre_logements: technique.nombre_logements || 1, nombre_locaux_professionnels: technique.nombre_locaux_professionnels || 0,
        nombre_niveaux: technique.nombre_niveaux || 1, nombre_sous_sols: technique.nombre_sous_sols || 0,
        duree_travaux_mois: technique.duree_travaux_mois || 12, surface_piscine: technique.surface_piscine || null,
        part_entreprises_ess: technique.part_entreprises_ess || 0,
      };
      const cleanedOperation = { ...operation, cout_global_ttc: operation.cout_global_ttc || null };
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/simulate`,
        { identite, operation: cleanedOperation, technique: cleanedTechnique, etudes, intervenants }, { headers });
      if (res.data.success) {
        if (!isExemptFromCredits) {
          const consumed = await consumeCredits(DOMIUM_SIMULATION_CREDIT_COST, `Simulation DOMIUM - ${res.data.reference}`);
          if (consumed && !silent) toast.info(`${DOMIUM_SIMULATION_CREDIT_COST} credits utilises`);
        }
        setSavedSimulation(res.data);
        if (!silent) toast.success(`Simulation creee : ${res.data.reference}`);
        fetchSimulations(); // Non-bloquant (pas de await)
        if (!silent) setActiveTab('resultat');
        return res.data;
      }
      return null;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
      return null;
    }
    finally { setSaving(false); }
  };

  // Admin config is now in hooks/useAdminConfig.js

  const handleDuplicateSimulation = async (reference) => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/${reference}/duplicate`, {}, { headers });
      if (res.data.success) {
        toast.success(`Simulation dupliquee : ${res.data.reference}`);
        await fetchSimulations();
        await handleLoadServerSimulation(res.data.reference);
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Erreur lors de la duplication')); }
    finally { setLoading(false); }
  };

  const handleDeleteSimulation = async (reference) => {
    if (!window.confirm(`Supprimer la simulation ${reference} ?`)) return;
    setLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
      toast.success(`Simulation ${reference} supprimee`);
      await fetchSimulations();
    } catch (error) { toast.error(getErrorMessage(error, 'Erreur suppression')); }
    finally { setLoading(false); }
  };

  return {
    isExemptFromCredits, isSuperAdmin, headers,
    loading, calculating, saving, config, setConfig,
    isFtpeMember, creditBalance, creditLoading, fetchCreditBalance,
    simulations, fetchSimulations, handleLoadServerSimulation, handleSaveSimulation, handleDuplicateSimulation, handleDeleteSimulation,
  };
}
